/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { checkEmailOrderConfirmationRoleAsync } from './DataService/DataActionExtension.g';
import { ICheckEmailOrderConfirmationRoleResponse } from './DataService/DataServiceEntities.g';

export class CheckEmailOrderConfirmationRoleInput implements IActionInput {
    public accountNum: number;
    public email: string;
    public cacheKeySuffix: string;

    constructor(accountNum: number | string, email: string, cacheKeySuffix?: string) {
        this.accountNum = typeof accountNum === 'string' ? +accountNum : accountNum;
        this.email = email;
        this.cacheKeySuffix = cacheKeySuffix || '';
    }

    // Cache the results for 30 minutes since user email preferences don't change frequently
    // but may be updated occasionally through customer service or account settings
    public getCacheKey = () => `CheckEmailOrderConfirmationRole_${this.accountNum}_${this.email}_${this.cacheKeySuffix}`;
    public getCacheObjectType = () => 'CheckEmailOrderConfirmationRoleResponse';
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Action function to check email order confirmation role
 */
export async function checkEmailOrderConfirmationRole(
    input: CheckEmailOrderConfirmationRoleInput,
    ctx: IActionContext
): Promise<ICheckEmailOrderConfirmationRoleResponse> {
    return checkEmailOrderConfirmationRoleAsync({ callerContext: ctx }, input.accountNum, input.email)
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[check-email-order-confirmation-role] Unable to load email confirmation role');
            throw new Error('[check-email-order-confirmation-role] Unable to load email confirmation role');
        });
}

/**
 * Create input function for the action
 */
const createInput = (args: ICreateActionContext, accountNum: number | string, email: string): IActionInput => {
    const cacheKeySuffix = args.requestContext?.app?.platform?.cacheKeySuffix || '';

    return new CheckEmailOrderConfirmationRoleInput(accountNum, email, cacheKeySuffix);
};

export default createObservableDataAction({
    action: <IAction<ICheckEmailOrderConfirmationRoleResponse>>checkEmailOrderConfirmationRole,
    id: 'CheckEmailOrderConfirmationRole',
    input: createInput
});
